<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import type * as Phaser from "phaser";
    import type { Unsubscriber } from "svelte/store";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import type { GameScene } from "../../Phaser/Game/GameScene";

    let canvas: HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D;
    let animationFrameId: number;
    let mapSnapshot: HTMLCanvasElement | null = null;
    let mapW = 0;
    let mapH = 0;
    let minimapW = 0;
    let minimapH = 0;
    let mapScale = 0;
    let mapOffsetX = 0;
    let mapOffsetY = 0;
    let containerEl: HTMLDivElement;
    let wokaImg: HTMLImageElement | null = $state(null);
    let unsubscribePicture: Unsubscriber | undefined;

    const MAX_DIM = 200;
    const WOKA_SIZE = 14;

    onMount(() => {
        console.log("[Minimap] mounted");
        ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
        startUpdateLoop();

        const scene = gameManager.getCurrentGameScene();
        // eslint-disable-next-line svelte/require-store-reactive-access
        if (scene?.CurrentPlayer?.pictureStore) {
            unsubscribePicture = scene.CurrentPlayer.pictureStore.subscribe((src) => {
                if (src) {
                    const img = new Image();
                    img.src = src;
                    img.onload = () => {
                        wokaImg = img;
                    };
                }
            });
        }
    });

    onDestroy(() => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        if (unsubscribePicture) unsubscribePicture();
    });

    function startUpdateLoop() {
        let wasReady = false;
        const update = () => {
            const scene = gameManager.getCurrentGameScene();
            if (!scene || !scene.cameras.main || !scene.Map) {
                animationFrameId = requestAnimationFrame(update);
                return;
            }

            const camera = scene.cameras.main;
            if (!wasReady) {
                wasReady = true;
                mapW = scene.Map.widthInPixels;
                mapH = scene.Map.heightInPixels;

                // Minimap respects map aspect ratio
                const aspect = mapW / mapH;
                if (aspect >= 1) {
                    minimapW = MAX_DIM;
                    minimapH = Math.round(MAX_DIM / aspect);
                } else {
                    minimapH = MAX_DIM;
                    minimapW = Math.round(MAX_DIM * aspect);
                }

                console.log("[Minimap] scene ready, map:", mapW, "x", mapH, "minimap:", minimapW, "x", minimapH);

                // Update canvas size to match aspect ratio
                canvas.width = minimapW;
                canvas.height = minimapH;

                captureMapSnapshot(scene);
            }

            drawMinimap(camera, scene);
            animationFrameId = requestAnimationFrame(update);
        };
        animationFrameId = requestAnimationFrame(update);
    }

    function captureMapSnapshot(scene: GameScene) {
        const tilemap: Phaser.Tilemaps.Tilemap = scene.Map;
        if (!tilemap || !tilemap.layers || mapW <= 0) return;

        const maxDim = 1024;
        const captureScale = Math.min(1, maxDim / Math.max(mapW, mapH));
        const captureW = Math.ceil(mapW * captureScale);
        const captureH = Math.ceil(mapH * captureScale);

        const offscreen = document.createElement("canvas");
        offscreen.width = captureW;
        offscreen.height = captureH;
        const octx = offscreen.getContext("2d");
        if (!octx) return;

        octx.fillStyle = "#1e293b";
        octx.fillRect(0, 0, captureW, captureH);
        octx.scale(captureScale, captureScale);

        const layers = tilemap.layers;
        const tileW = tilemap.tileWidth;
        const tileH = tilemap.tileHeight;

        for (const layer of layers) {
            if (!layer.visible || !layer.data) continue;
            const data: Phaser.Tilemaps.Tile[][] = layer.data;
            for (let row = 0; row < data.length; row++) {
                const tileRow = data[row];
                if (!tileRow) continue;
                for (let col = 0; col < tileRow.length; col++) {
                    const tile = tileRow[col];
                    if (!tile || tile.index < 0) continue;
                    const set = tile.tileset;
                    if (!set) continue;
                    const image = set.image?.getSourceImage();
                    if (!image) continue;
                    const localIdx = tile.index - set.firstgid;
                    const setCol = localIdx % set.columns;
                    const setRow = Math.floor(localIdx / set.columns);
                    const sx = (set.tileMargin ?? 0) + setCol * ((set.tileWidth ?? tileW) + (set.tileSpacing ?? 0));
                    const sy = (set.tileMargin ?? 0) + setRow * ((set.tileHeight ?? tileH) + (set.tileSpacing ?? 0));
                    try {
                        octx.drawImage(
                            image as HTMLCanvasElement,
                            sx,
                            sy,
                            set.tileWidth ?? tileW,
                            set.tileHeight ?? tileH,
                            col * tileW,
                            row * tileH,
                            tileW,
                            tileH,
                        );
                    } catch (e) {
                        console.debug(e);
                    }
                }
            }
        }

        octx.setTransform(1, 0, 0, 1, 0, 0);
        mapSnapshot = offscreen;
        console.log("[Minimap] snapshot captured:", captureW, "x", captureH);
    }

    function drawMinimap(camera: Phaser.Cameras.Scene2D.Camera, scene: GameScene) {
        if (!ctx || !canvas || minimapW <= 0 || minimapH <= 0) return;

        ctx.clearRect(0, 0, minimapW, minimapH);

        mapScale = Math.min(minimapW / mapW, minimapH / mapH);
        mapOffsetX = Math.round((minimapW - mapW * mapScale) / 2);
        mapOffsetY = Math.round((minimapH - mapH * mapScale) / 2);

        const scaledW = Math.round(mapW * mapScale);
        const scaledH = Math.round(mapH * mapScale);

        if (mapSnapshot) {
            ctx.drawImage(mapSnapshot, mapOffsetX, mapOffsetY, scaledW, scaledH);
        } else {
            ctx.fillStyle = "#1e293b";
            ctx.fillRect(mapOffsetX, mapOffsetY, scaledW, scaledH);
        }

        // Use camera's internal coordinate system
        const topLeft = camera.getWorldPoint ? camera.getWorldPoint(0, 0) : { x: camera.scrollX, y: camera.scrollY };
        const bottomRight = camera.getWorldPoint
            ? camera.getWorldPoint(camera.width, camera.height)
            : { x: camera.scrollX + camera.width / camera.zoom, y: camera.scrollY + camera.height / camera.zoom };

        const vLeft = Math.max(0, topLeft.x);
        const vTop = Math.max(0, topLeft.y);
        const vRight = Math.min(mapW, bottomRight.x);
        const vBottom = Math.min(mapH, bottomRight.y);
        const vW = vRight - vLeft;
        const vH = vBottom - vTop;

        const miniX = mapOffsetX + vLeft * mapScale;
        const miniY = mapOffsetY + vTop * mapScale;
        const miniW = vW * mapScale;
        const miniH = vH * mapScale;

        ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        ctx.fillRect(0, 0, minimapW, miniY);
        ctx.fillRect(0, miniY + miniH, minimapW, minimapH - miniY - miniH);
        ctx.fillRect(0, miniY, miniX, miniH);
        ctx.fillRect(miniX + miniW, miniY, minimapW - miniX - miniW, miniH);

        ctx.strokeStyle = "rgba(34, 197, 94, 0.25)";
        ctx.lineWidth = 2;
        ctx.strokeRect(miniX, miniY, miniW, miniH);

        if (scene.CurrentPlayer) {
            const px = mapOffsetX + scene.CurrentPlayer.x * mapScale;
            const py = mapOffsetY + scene.CurrentPlayer.y * mapScale;

            if (wokaImg) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(px, py, WOKA_SIZE / 2, 0, Math.PI * 2);
                ctx.clip();
                ctx.drawImage(wokaImg, px - WOKA_SIZE / 2, py - WOKA_SIZE / 2, WOKA_SIZE, WOKA_SIZE);
                ctx.restore();
                ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(px, py, WOKA_SIZE / 2, 0, Math.PI * 2);
                ctx.stroke();
            } else {
                ctx.fillStyle = "rgba(239, 68, 68, 0.4)";
                ctx.beginPath();
                ctx.arc(px, py, 6, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "#ef4444";
                ctx.beginPath();
                ctx.arc(px, py, 3, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.strokeStyle = "rgba(255,255,255,0.3)";
        ctx.lineWidth = 1;
        ctx.strokeRect(mapOffsetX, mapOffsetY, scaledW, scaledH);
    }

    function handleClick(e: MouseEvent) {
        const scene = gameManager.getCurrentGameScene();
        if (!scene || minimapW <= 0) return;
        const rect = canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const wx = Math.max(0, Math.min(mapW, (cx - mapOffsetX) / mapScale));
        const wy = Math.max(0, Math.min(mapH, (cy - mapOffsetY) / mapScale));
        const cameraManager = scene.getCameraManager();
        if (cameraManager) {
            cameraManager.centerCameraOn({ x: wx, y: wy }, undefined);
        }
    }
</script>

<div class="minimap-container" bind:this={containerEl}>
    <canvas bind:this={canvas} width={minimapW || MAX_DIM} height={minimapH || MAX_DIM} onclick={handleClick}></canvas>
</div>

<style>
    .minimap-container {
        position: fixed;
        bottom: 8px;
        right: 60px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-radius: 10px;
        overflow: hidden;
        background: rgba(15, 23, 42, 0.85);
        backdrop-filter: blur(4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
        z-index: 1;
        cursor: crosshair;
    }

    canvas {
        display: block;
    }
</style>
